import { Spinner } from '@forest/shared/solid'
import { createSignal, Index, Match, Switch, type Accessor } from 'solid-js'
import { logNonFatal } from '~/lib/diagnostics'
import { m } from '~/paraglide/messages'
import { errorDetail } from '~/services/bootstrap'
import { fetchTreeArt } from '~/services/catalogue'
import type { StarterPack, StarterPackContent } from '~/services/starter-pack'
import type { ShopBalances } from '~/store/session-ledger'
import {
  createCurrencyFlight,
  CurrencyFlightLayer,
  currencyPresentation,
} from './currency-reward-reveal'
import type { RewardRevealVariant } from './reward-reveal-backdrop'
import revealStepCheckedIcon from './icons/reveal-step-checked.svg'
import revealStepPendingIcon from './icons/reveal-step-pending.svg'
import { RewardRevealScaffold } from './reward-reveal-scaffold'

// One page's art. A currency's own icon falls back to the built-in one when
// it fails to load; a tree's species art is a native lookup that may still be
// running when its page shows.
type PageArt =
  | { status: 'loading' }
  | { status: 'none' }
  | { fallbackSrc?: string; src: string; status: 'ready' }

/**
 * Starts resolving every page's art (GLOSSARY.md "Reward reveal"): tree
 * species art from the native catalogue, land-skin art — the pack content's
 * own `icon_image_url`, else the land skin's icon wherever Shop's catalogue
 * holds it, else the pack's own detail image for that content — and a currency's `icon_image_url`, falling back to the
 * built-in currency icon. Call it when the purchase starts, so the art is
 * there by the time the reveal opens.
 */
export function createStarterPackRevealArt(
  pack: StarterPack,
  options: { landSkinIconUrl: (gid: number) => string | undefined },
): Accessor<PageArt[]> {
  const ready = (src: string | undefined): PageArt =>
    src ? { src, status: 'ready' } : { status: 'none' }
  const initial = pack.contents.map((content, index): PageArt => {
    if (content.kind === 'currency') {
      const builtIn = currencyPresentation[content.content].icon
      return content.iconUrl
        ? { fallbackSrc: builtIn, src: content.iconUrl, status: 'ready' }
        : { src: builtIn, status: 'ready' }
    }
    if (content.content === 'land_skin') {
      return ready(
        content.iconUrl ??
          options.landSkinIconUrl(content.gid) ??
          pack.detailImageUrls[index],
      )
    }
    return { status: 'loading' }
  })
  const [art, setArt] = createSignal(initial)
  pack.contents.forEach((content, index) => {
    if (content.kind !== 'catalogue' || content.content !== 'tree_type') return
    void fetchTreeArt(content.gid)
      .then(
        (image) => image?.src,
        (error: unknown) => {
          logNonFatal('shop_starter_pack_reveal_art_failed', {
            detail: errorDetail(error),
            gid: String(content.gid),
          })
          return undefined
        },
      )
      .then((src) =>
        setArt((current) =>
          current.map((page, at) => (at === index ? ready(src) : page)),
        ),
      )
  })
  return art
}

function PageArtwork(props: { art: PageArt | undefined }) {
  const [failedSrc, setFailedSrc] = createSignal<string>()
  const src = () => {
    const art = props.art
    if (art?.status !== 'ready') return undefined
    return failedSrc() === art.src ? art.fallbackSrc : art.src
  }
  return (
    <div
      data-testid="starter-pack-reward-artwork"
      data-artwork-status={props.art?.status ?? 'none'}
      class="relative flex size-full items-center justify-center"
    >
      <Switch>
        <Match when={props.art?.status === 'loading'}>
          <div aria-hidden="true">
            <Spinner class="size-5 text-white/80" />
          </div>
        </Match>
        <Match when={src()}>
          {(source) => (
            <img
              src={source()}
              alt=""
              draggable={false}
              onError={() => setFailedSrc(source())}
              class="size-full object-contain"
            />
          )}
        </Match>
      </Switch>
    </div>
  )
}

// Every page reached so far — the showing one included — is a white
// checkmark; the rest are faint white dots (the design's reward_dots).
function RevealProgress(props: { count: number; page: number }) {
  return (
    <ol
      aria-label={`${props.page + 1} / ${props.count}`}
      class="mt-6 flex items-center justify-center gap-3"
    >
      <Index each={Array.from({ length: props.count })}>
        {(_step, index) => (
          <li
            data-testid="starter-pack-reward-step"
            data-checked={String(index <= props.page)}
            class="flex items-center"
          >
            <img
              alt=""
              src={
                index <= props.page
                  ? revealStepCheckedIcon
                  : revealStepPendingIcon
              }
              class={index <= props.page ? 'h-3 w-3.5' : 'size-3'}
            />
          </li>
        )}
      </Index>
    </ol>
  )
}

const pageVariant = (content: StarterPackContent): RewardRevealVariant =>
  content.kind === 'catalogue' ? 'tree' : content.content

/**
 * The starter pack's multi-item Reward reveal (GLOSSARY.md "Reward reveal"):
 * one page per content in content order, each with its own Claim, above a
 * progress indicator. A coin or Time Crystal page's Claim flies the currency
 * to its pill and credits the session ledger on arrival, then advances; a
 * tree or land-skin page's Claim advances directly. The last Claim closes the
 * reveal (onDone).
 */
export function StarterPackRewardReveal(props: {
  art: Accessor<PageArt[]>
  balances: Accessor<ShopBalances | undefined>
  credit: (currency: keyof ShopBalances, amount: number) => void
  onDone: () => void
  pack: StarterPack
}) {
  const [page, setPage] = createSignal(0)
  const flight = createCurrencyFlight({
    balances: () => props.balances(),
    onArrival: (grant) => {
      props.credit(grant.currency, grant.amount)
      advance()
    },
  })
  const content = () => props.pack.contents[page()]!
  const advance = () => {
    if (page() >= props.pack.contents.length - 1) props.onDone()
    else setPage(page() + 1)
  }
  const claim = () => {
    if (flight.flying()) return
    const current = content()
    if (current.kind === 'catalogue') {
      advance()
      return
    }
    flight.launch({ amount: current.amount, currency: current.content })
  }

  return (
    <>
      <RewardRevealScaffold
        balanceTestIdPrefix="starter-pack-reward-"
        caption={props.pack.captions[page()] ?? ''}
        captionTestId="starter-pack-reward-caption"
        coinPillRef={flight.coinPillRef}
        coinValue={flight.displayedBalance('coin')}
        ctaDisabled={flight.flying()}
        ctaLabel={m.claim()}
        ctaTestId="starter-pack-reward-claim"
        gemPillRef={flight.gemPillRef}
        gemValue={flight.displayedBalance('gem')}
        onCta={claim}
        phase={flight.flying() ? 'flight' : 'ready'}
        progress={
          <RevealProgress count={props.pack.contents.length} page={page()} />
        }
        rewardAnchorRef={flight.rewardAnchorRef}
        page={page()}
        testId="starter-pack-reward-reveal"
        title={m.claimed_title()}
        variant={pageVariant(content())}
      >
        <PageArtwork art={props.art()[page()]} />
      </RewardRevealScaffold>

      <CurrencyFlightLayer
        flight={flight}
        testId="starter-pack-reward-flight"
      />
    </>
  )
}
